import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { getUserDetailsById, updateUserDetails } from '../api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";

// --- ASSET IMPORTS ---
import editIcon from '../assets/edit.png'; 
import infoIcon from '../assets/info.png';       
import pieChartIcon from '../assets/piechart.png'; 
import printIcon from '../assets/print.png'; 

const API_BASE_URL = '/vres/users';
const PROJECTS_API_URL = '/vres/projects';

// --- STYLING CONSTANTS ---
const THEME = {
    accent: '#20c997', // Mint
    sidebarBg: '#1c3334',
    bg: '#f0fdf9',
    textDark: '#2d3436',
    textLight: '#636e72',
    cardBg: '#ffffff',
    border: '#e1e4e8',
    colors: {
        'ADMIN': "#dc3545",
        'PROJECT COORDINATOR': "#f59e0b",
        'VENDOR': "#8b5cf6",
        'ISSUER': "#3ab5c8",
        'MAKER': "#10b981",
        'CHECKER': "#3b82f6",
        'OBSERVER': "#f17735",
        'NO ROLE': "#6c757d",
    }
};

// --- CSS STYLES (Injected) ---
const DashboardStyles = () => (
    <style>{`
        :root {
            --sidebar-bg: #1c3334;
            --accent-color: #20c997;
            --bg-color: #f0fdf9;
            --card-bg: #ffffff;
            --text-dark: #2d3436;
            --text-light: #636e72;
            --border-color: #e1e4e8;
        }

        body { background-color: var(--bg-color); font-family: 'Segoe UI', Roboto, sans-serif; }

        /* --- LAYOUT FIXES --- */
        .dashboard-wrapper { 
            padding: 30px; 
            max-width: 1400px; 
            margin: 0 auto; 
            width: 100%;
            overflow-y: auto;
        }
        
        /* --- CONTROL BAR --- */
        .control-bar {
            background: var(--card-bg); padding: 12px 20px; border-radius: 8px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.03); display: flex;
            justify-content: space-between; align-items: center; margin-bottom: 20px;
            border: 1px solid var(--border-color);
            position: relative; 
            z-index: 5; 
        }
        .project-select {
            padding: 8px; border: 1px solid var(--border-color);
            border-radius: 6px; width: 280px; outline: none; font-size: 0.9rem;
            cursor: pointer;
        }
        .btn-print {
            background-color: var(--sidebar-bg); color: white;
            padding: 8px 16px; border: none; border-radius: 6px; cursor: pointer;
            display: flex; align-items: center; gap: 8px; transition: opacity 0.2s; font-size: 0.85rem;
            font-weight: 600;
        }
        .btn-print:hover { opacity: 0.9; }
        .btn-print img {
            width: 18px; height: 18px; object-fit: contain; filter: brightness(0) invert(1); 
        }

        /* --- STATS GRID --- */
        .stats-grid {
            display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
            margin-bottom: 20px; align-items: stretch;
            min-height: 280px; 
        }
        .dashboard-card {
            background: var(--card-bg); padding: 20px; border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.03); border: 1px solid var(--border-color);
            height: 100%; display: flex; flex-direction: column;
            overflow: hidden; 
        }
        .dashboard-card h3 {
            font-size: 1rem; margin-bottom: 15px; color: var(--text-dark);
            border-bottom: 1px solid var(--bg-color); padding-bottom: 10px;
            display: flex; align-items: center; gap: 10px; margin-top: 0;
            flex-shrink: 0;
            font-weight: 700;
        }
        
        .title-icon {
            width: 20px; height: 20px; object-fit: contain;
        }

        /* --- OVERVIEW CONTENT --- */
        .overview-layout {
            display: flex; justify-content: space-between; align-items: center; flex-grow: 1;
        }
        .overview-details { display: flex; flex-direction: column; gap: 15px; }
        .detail-item label { font-size: 0.8rem; color: var(--text-light); display: block; margin-bottom: 3px; }
        .detail-item span { font-size: 1.1rem; font-weight: 700; color: var(--sidebar-bg); display: block; }
        
        .overview-total {
            text-align: center; background-color: #f0fdf9; padding: 15px 25px;
            border-radius: 10px; border: 1px solid #d1fae5; min-width: 130px;
        }
        .overview-total .label { font-size: 0.85rem; color: var(--text-light); margin-bottom: 5px; }
        .overview-total .number { font-size: 2.5rem; font-weight: 700; color: var(--accent-color); line-height: 1; }

        /* --- TABLE STYLES --- */
        .table-header-flex {
            padding: 15px 20px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #eee;
        }
        .search-box { position: relative; }
        .search-box input {
            padding: 8px 15px 8px 35px; border: 1px solid var(--border-color);
            border-radius: 20px; outline: none; width: 250px; font-size: 0.85rem;
        }
        .search-box i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aaa; font-size: 0.9rem; }

        .custom-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .custom-table thead th {
            text-align: left; padding: 12px 15px; background-color: #f8f9fa;
            color: var(--text-light); border-bottom: 2px solid var(--border-color); font-weight: 600;
        }
        .custom-table tbody td {
            padding: 12px 15px; border-bottom: 1px solid var(--border-color);
            color: var(--text-dark); vertical-align: middle;
        }
        .custom-table tbody tr:hover { background-color: #fcfcfc; }

        /* --- BADGES --- */
        .badge-custom { padding: 4px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 600; display: inline-block; letter-spacing: 0.3px; }
        .role-coordinator { background: #fff8e1; color: #b45309; }
        .role-maker { background: #ecfdf5; color: #047857; }
        .role-checker { background: #eff6ff; color: #1d4ed8; }
        .role-vendor { background: #f5f3ff; color: #6d28d9; }
        .role-issuer { background: #ecfeff; color: #0e7490; }
        .role-observer { background: #fff7ed; color: #c2410c; }
        .role-admin { background: #fef2f2; color: #b91c1c; }
        .role-default { background: #f3f4f6; color: #374151; }

        /* Updated Image Button Style */
        .btn-img-action {
            background: none; border: none; padding: 4px; cursor: pointer;
            transition: transform 0.2s; display: inline-flex; align-items: center; justify-content: center;
            border-radius: 4px;
        }
        .btn-img-action:hover { transform: scale(1.1); background-color: #f0f0f0; }
        .btn-img-action img {
            width: 20px; height: 20px; 
            object-fit: contain;
        }

        @media (max-width: 992px) {
            .stats-grid { 
                grid-template-columns: 1fr; 
                height: auto; 
            }
            .dashboard-card { min-height: 250px; margin-bottom: 20px; }
        }

        /* --- 🔴 CRITICAL PRINT STYLES FOR MULTIPAGE --- */
        @media print {
            @page {
                size: A4;
                margin: 10mm;
            }
            body, html, #root {
                height: auto !important;
                min-height: auto !important;
                overflow: visible !important;
                background-color: white !important;
            }
            .no-print, .control-bar, .search-box, button, .topHeader { 
                display: none !important; 
            }
            .dashboard-wrapper { 
                position: static !important;
                max-width: 100% !important; 
                width: 100% !important;
                padding: 0 !important; 
                margin: 0 !important;
                height: auto !important;
                overflow: visible !important;
                display: block !important;
            }
            .dashboard-card { 
                box-shadow: none !important; 
                border: 1px solid #ccc !important;
                break-inside: avoid;
                page-break-inside: avoid;
                margin-bottom: 20px !important;
                height: auto !important;
                display: block !important;
            }
            .stats-grid {
                display: block !important; /* Stack stats vertically on print if needed, or keep grid if it fits */
                page-break-inside: avoid;
            }
            /* Unlock the table container */
            div[style*="overflow"] {
                overflow: visible !important;
                height: auto !important;
            }
            table {
                page-break-inside: auto;
            }
            tr {
                page-break-inside: avoid;
                page-break-after: auto;
            }
        }
    `}</style>
);

// --- COMPONENT: Reset Password Modal ---
const ResetPasswordModal = ({ userId, onClose }) => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newPassword || !confirmPassword) return toast.warn("Please fill in both fields.");
        if (newPassword !== confirmPassword) return toast.error("Passwords do not match.");
        if (newPassword.length < 6) return toast.warn("Password must be at least 6 characters.");

        setLoading(true);
        try {
            await api.post(`${API_BASE_URL}/${userId}/reset-password`, { password: newPassword });
            toast.success("Password reset successfully! Email sent to user.");
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to reset password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.overlayHighZ}>
            <div style={styles.popup}>
                <h4 style={{ color: '#007bff', textAlign: 'center', marginBottom: '20px', fontWeight: 'bold' }}>Reset Password</h4>
                <form onSubmit={handleSubmit}>
                    <div className="form-group mb-3">
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>Enter New Password</label>
                        <input type="password" style={styles.input} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" required />
                    </div>
                    <div className="form-group mb-4">
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>Confirm Password</label>
                        <input type="password" style={styles.input} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm Password" required />
                    </div>
                    <div className="d-flex justify-content-end gap-2" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button type="button" style={styles.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
                        <button type="submit" style={styles.btnDanger} disabled={loading}>{loading ? "Processing..." : "Reset Password"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- COMPONENT: Edit User Modal ---
const EditUserModal = ({ isModalOpen, closeModal, user, onSave }) => {
    const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
    const [showResetPassword, setShowResetPassword] = useState(false);

    useEffect(() => {
        if (user) setFormData({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
        if (!isModalOpen) setShowResetPassword(false);
    }, [user, isModalOpen]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        const validId = user.userId || user.id;
        if (user && validId) onSave(validId, formData);
        else toast.error("Error: User ID is missing.");
    };

    if (!isModalOpen || !user) return null;
    const validUserId = user.userId || user.id;

    return (
        <>
            <div style={styles.overlay}>
                <div style={styles.modalCard}>
                    <div style={{ textAlign: 'center', marginBottom: '25px', borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                        <h4 style={{ color: THEME.accent, fontWeight: 'bold', margin: 0 }}>Edit User Details</h4>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={styles.label}>Name</label>
                            <input type="text" style={styles.input} name="name" value={formData.name} onChange={handleChange} required />
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={styles.label}>Email (User ID)</label>
                            <input type="email" style={styles.input} name="email" value={formData.email} onChange={handleChange} required />
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={styles.label}>Mobile Number</label>
                            <input type="text" style={styles.input} name="phone" value={formData.phone} onChange={handleChange} required placeholder="Enter 10-digit mobile number" />
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '30px' }}>
                            <button type="button" style={{ ...styles.btnOutlineDanger, fontSize: '0.85rem' }} onClick={() => setShowResetPassword(true)}>
                                <i className="fa-solid fa-key" style={{ marginRight: '5px' }}></i> Reset Password
                            </button>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button type="button" style={styles.btnSecondary} onClick={closeModal}>Cancel</button>
                                <button type="submit" style={styles.btnPrimary}>Update</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            {showResetPassword && <ResetPasswordModal userId={validUserId} onClose={() => setShowResetPassword(false)} />}
        </>
    );
};

// --- COMPONENT: Chart ---
const UserRoleChart = ({ data }) => {
    const hasData = data && data.some(d => d.value > 0);

    return (
        <div className="dashboard-card" style={{ padding: '15px' }}>
            {/* ✅ Icon added left of Title */}
            <h3>
                <img src={pieChartIcon} alt="Chart" className="title-icon" /> 
                User Role Distribution
            </h3>
            {!hasData ? (
                <p className="text-center text-muted py-4">No user data available.</p>
            ) : (
                <div style={{ flexGrow: 1, width: '100%', minHeight: '0' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={70}
                                paddingAngle={3}
                                labelLine={false}
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={THEME.colors[entry.name] || THEME.colors['NO ROLE']} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }} />
                            <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ fontSize: '11px', paddingTop: '0px' }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

// --- MAIN PAGE COMPONENT ----------------------------------------
const UserDashboardPage = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('currentUser'));
        if (storedUser) setCurrentUser(storedUser);
    }, []);

    const handlePrint = () => window.print();

    // --- 1. Fetch Project List (Updated for Observer Restriction) ---
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem("currentUser"));
                const userRole = storedUser?.role;
                
                // Fetch all projects first
                const response = await api.get(PROJECTS_API_URL);
                let availableProjects = response.data;

                // [NEW LOGIC] If Observer, filter projects to ONLY the assigned one
                if (userRole === 'OBSERVER') {
                    // 1. Try to find project from local storage details
                    if (storedUser?.projects && storedUser.projects.length > 0) {
                        const assignedId = storedUser.projects[0].projectId || storedUser.projects[0].id;
                        
                        // Filter the API list to only the assigned project
                        availableProjects = availableProjects.filter(p => (p.projectId || p.id) === assignedId);
                        
                        // Auto-select the project immediately
                        if (availableProjects.length > 0) {
                            setSelectedProjectId(assignedId);
                        }
                    } else {
                        // 2. Fallback: If local storage is empty, try to derive from the filtered list 
                        // (assuming backend might naturally filter the list for observers in the future)
                        if(availableProjects.length === 1) {
                             setSelectedProjectId(availableProjects[0].projectId || availableProjects[0].id);
                        }
                    }
                }

                setProjects(availableProjects);

            } catch (err) {
                console.error("Error fetching projects:", err);
                // Don't show error toast immediately for Observers if list is empty, might just be loading
                if (JSON.parse(localStorage.getItem("currentUser"))?.role !== 'OBSERVER') {
                    toast.error("Failed to load project list.");
                }
            }
        };
        fetchProjects();
    }, []);

    // --- 2. Fetch Users (Updated to handle Observer response) ---
    const fetchDashboardData = useCallback(async () => {
        // If Observer, we might not have a selectedProjectId yet, but the backend 
        // handles it automatically now. However, strictly for UI consistency:
        if (!selectedProjectId && currentUser?.role !== 'OBSERVER') {
            setUsers([]);
            return;
        }

        setIsLoading(true);
        try {
            // The backend now detects OBSERVER role and returns the correct data 
            // regardless of the projectId param, but we pass it for consistency.
            const response = await api.get(`${API_BASE_URL}/dashboard`, { 
                params: { projectId: selectedProjectId || 0 } 
            });
            
            setUsers(response.data);
            
            // [OPTIONAL] If Observer and selectedProjectId was missing, 
            // we can try to extract it from the response to update the UI title
            if (currentUser?.role === 'OBSERVER' && !selectedProjectId && response.data.length > 0) {
                const user = response.data[0];
                if (user.assignments && user.assignments.length > 0) {
                     setSelectedProjectId(user.assignments[0].projectId);
                }
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching user dashboard data:", err);
            setError("Failed to load user data.");
            toast.error("Failed to load user data.");
            if (err.response && err.response.status === 401) navigate('/login');
        } finally {
            setIsLoading(false);
        }
    }, [selectedProjectId, navigate, currentUser]);
    
    useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

    const currentProjectName = useMemo(() => {
        const p = projects.find(proj => String(proj.projectId || proj.id) === String(selectedProjectId));
        return p ? (p.projectName || p.title) : 'N/A';
    }, [projects, selectedProjectId]);

    const roleChartData = useMemo(() => {
        const roleCounts = {};
        users.forEach(user => {
            const matchingAssignment = user.assignments && user.assignments.find(
                a => String(a.projectId) === String(selectedProjectId)
            );
            const roleName = matchingAssignment ? matchingAssignment.roleName.toUpperCase() : 'NO ROLE';
            roleCounts[roleName] = (roleCounts[roleName] || 0) + 1;
        });
        return Object.keys(roleCounts).map(roleName => ({
            name: roleName,
            value: roleCounts[roleName],
        })).sort((a, b) => b.value - a.value);
    }, [users, selectedProjectId]);

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.includes(searchTerm))
    );

    const handleEditClick = async (userId) => {
        try {
            const res = await getUserDetailsById(userId);
            setSelectedUser(res.data);
            setIsModalOpen(true);
        } catch (error) {
            toast.error("Failed to fetch user details.");
        }
    };

    const handleUpdateUser = async (userId, data) => {
        try {
            await updateUserDetails(userId, data);
            toast.success(`User updated successfully!`);
            setIsModalOpen(false);
            setSelectedUser(null);
            setUsers(prevUsers => prevUsers.map(user => user.id === userId ? { ...user, ...data } : user));
            fetchDashboardData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update user.");
        }
    };

    const getRoleBadgeClass = (roleName) => {
        const r = roleName ? roleName.toUpperCase() : '';
        if (r.includes('COORDINATOR')) return 'role-coordinator';
        if (r.includes('MAKER')) return 'role-maker';
        if (r.includes('ISSUER')) return 'role-issuer';
        if (r.includes('CHECKER')) return 'role-checker';
        if (r.includes('VENDOR')) return 'role-vendor';
        if (r.includes('OBSERVER')) return 'role-observer';
        if (r.includes('ADMIN')) return 'role-admin';
        return 'role-default';
    };

    const renderPrimaryRole = (assignments) => {
        if (!assignments) return <span className="text-muted small">No Role</span>;
        const projectRole = assignments.find(a => String(a.projectId) === String(selectedProjectId));
        if (projectRole) {
            return <span className={`badge-custom ${getRoleBadgeClass(projectRole.roleName)}`}>{projectRole.roleName}</span>;
        }
        return <span className="text-muted small">N/A</span>;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="dashboard-wrapper">
                <DashboardStyles />
                
                <div className="control-bar no-print">
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <label style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                            {currentUser?.role === 'OBSERVER' ? "Assigned Project:" : "Current Project:"}
                        </label>
                        <select
                            className="project-select"
                            value={selectedProjectId}
                            onChange={(e) => setSelectedProjectId(e.target.value)}
                            disabled={currentUser?.role === 'OBSERVER' && projects.length === 1}
                        >
                            <option value="">-- Choose a Project --</option>
                            {projects.map((proj) => (
                                <option key={proj.projectId || proj.id} value={proj.projectId || proj.id}>
                                    {proj.projectName || proj.title}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ color: '#636e72', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      {currentUser?.name || currentUser?.role || 'Administrator'} <span style={{marginLeft: '8px'}}></span>
                    </div>
                    {selectedProjectId && (
                        /* ✅ ADDED PRINT ICON TO BUTTON */
                        <button className="btn-print" onClick={handlePrint}>
                            <img src={printIcon} alt="Print" style={{width: '16px', marginRight: '6px'}}/> 
                            Print Report
                        </button>
                    )}
                </div>

                {!selectedProjectId ? (
                    <div className="text-center py-5 text-muted">
                        <p className="fs-5">Please select a project to view details.</p>
                    </div>
                ) : (
                    <>
                        {isLoading && <div className="text-center py-5">Loading...</div>}
                        {error && <div className="alert alert-danger text-center">{error}</div>}

                        {!isLoading && !error && (
                            <>
                                {/* --- STATS GRID --- */}
                                <div className="stats-grid">
                                    <div className="dashboard-card">
                                        {/* ✅ Icon added left of Title */}
                                        <h3>
                                            <img src={infoIcon} alt="Info" className="title-icon" />
                                            Project Overview
                                        </h3>
                                        <div className="overview-layout">
                                            <div className="overview-details">
                                                <div className="detail-item">
                                                    <label>Project Name</label>
                                                    <span>{currentProjectName}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Project ID</label>
                                                    <span>#{selectedProjectId}</span>
                                                </div>
                                            </div>
                                            <div className="overview-total">
                                                <div className="number">{users.length}</div>
                                                <div className="label">Total Users</div>
                                            </div>
                                        </div>
                                    </div>

                                    <UserRoleChart data={roleChartData} />
                                </div>

                                {/* --- TABLE CARD --- */}
                                <div className="dashboard-card" style={{ padding: 0, overflow: 'hidden' }}>
                                    <div className="table-header-flex">
                                        <h3 style={{ margin: 0, border: 'none', padding: 0 }}>User List Details</h3>
                                        <div className="search-box no-print">
                                            <i className="fa-solid fa-magnifying-glass" style={{position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#aaa'}}></i>
                                            <input 
                                                type="text" 
                                                placeholder="Search name, email..." 
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div style={{ overflowX: 'auto', flexGrow: 1 }}>
                                        <table className="custom-table">
                                            <thead>
                                                <tr>
                                                    <th style={{width: '20%'}}>Name/Email</th>
                                                    <th style={{width: '10%'}}>Phone</th>
                                                    <th style={{width: '10%'}}>Role</th>
                                                    <th style={{width: '20%'}}>Assignments</th>
                                                    <th style={{width: '20%'}}>Vendor Details</th>
                                                    {currentUser?.role === 'ADMIN' && <th style={{width: '5%', textAlign: 'center'}}>Edit</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredUsers.length > 0 ? filteredUsers.map((user) => (
                                                    <tr key={user.id}>
                                                        <td>
                                                            <div style={{ fontWeight: 'bold' }}>{user.name}</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#888' }}>{user.email}</div>
                                                        </td>
                                                        <td>{user.phone || '-'}</td>
                                                        <td>{renderPrimaryRole(user.assignments)}</td>
                                                        <td style={{ fontSize: '0.8rem', color: '#666' }}>
                                                            {user.assignments && user.assignments.map((a, i) => (
                                                                <div key={i}>{a.roleName} ({a.projectName})</div>
                                                            ))}
                                                        </td>
                                                        <td style={{ fontSize: '0.8rem' }}>
                                                            {user.vendorGst && <div><b>GST:</b> {user.vendorGst}</div>}
                                                            {user.vendorAddress && <div className="text-truncate" style={{maxWidth: '150px'}}><b>Addr:</b> {user.vendorAddress}</div>}
                                                        </td>
                                                        {currentUser?.role === 'ADMIN' && (
                                                            <td style={{ textAlign: 'center' }}>
                                                                <button className="btn-img-action" onClick={() => handleEditClick(user.id)} title="Edit User">
                                                                            <img src={editIcon} alt="Edit" />
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan="7" className="text-center py-3">No users found.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}

                <EditUserModal
                    isModalOpen={isModalOpen}
                    closeModal={() => setIsModalOpen(false)}
                    user={selectedUser}
                    onSave={handleUpdateUser}
                />
            </div>
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 },
    overlayHighZ: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 },
    
    // Updated Modal Card Style
    modalCard: { 
        width: '100%', maxWidth: '500px', backgroundColor: '#fff', borderRadius: '12px', border: 'none', padding: '30px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)', borderTop: '5px solid #20c997' // Teal Top Border
    },
    popup: { backgroundColor: "#fff", padding: "30px", borderRadius: "12px", width: "400px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)", borderTop: '5px solid #dc3545' }, // Red Top Border for Danger Action
    
    // Top Header
    topHeader: {
        backgroundColor: 'white', padding: '0 40px', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', borderBottom: '1px solid #e1e4e8', height: '70px', flexShrink: 0, zIndex: 100, position: 'sticky', top: 0
    },
    
    // Form Styles
    label: { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#2d3436', fontSize: '0.9rem' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #dcdcdc', borderRadius: '6px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' },
    
    // Button Styles
    btnPrimary: { backgroundColor: '#20c997', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' },
    btnSecondary: { backgroundColor: '#e9ecef', color: '#495057', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' },
    btnDanger: { backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' },
    btnOutlineDanger: { backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }
};

export default UserDashboardPage;